import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, TextField } from '@cere/cere-design-system';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { VaultRequestError } from '@cef-ai/vault-sdk';
import { claimVault } from '@/lib/clients/vault/claim';
import { toDisplayError } from '@/lib/vault/step-up-error';
import { useConfig } from '@/src/config/context';
import { useVaultSession } from '@/src/state/session';
import { ConnectStepRail } from '@/src/components/ConnectStepRail';
import {
  duplicateSeedNames,
  seedableRows,
  scopeSeedNameError,
  scopeSeedPathError,
  scopeSeedRowError,
  toCreateRequest,
  type ScopeSeedRow,
} from './scope-seed';
// ConnectStepRail ships `connect-rail` classes; its stylesheet is loaded by
// the /connect route, so a wizard mounted elsewhere has to pull it in itself.
import '@/src/styles/connect.css';
import '@/src/styles/orgs.css';

interface Props {
  open: boolean;
  /**
   * The caller's own vault id, or null when the wallet has none. Decides the
   * write branch: rename+seed when a vault exists, claim+seed when it does
   * not. Supplied as a prop (not re-derived here) because "does this wallet
   * own a vault" is the section's knowledge — the switcher's owner entry and
   * the session record agree on it there.
   */
  ownVaultId: string | null;
  onClose: () => void;
}

// Modeled on VaultConnectFlow's Step machine: one state per screen, with the
// async phases (running) and terminal states (done) as siblings rather than
// bolted-on booleans.
type Step = 'name' | 'scopes' | 'review' | 'running' | 'done';

const RAIL = [
  { key: 'name', label: 'Name the organization' },
  { key: 'scopes', label: 'Seed scopes' },
  { key: 'review', label: 'Review' },
];

const EMPTY_ROW: ScopeSeedRow = { name: '', path: '' };

export function OrgSetupWizard({ open, ownVaultId, onClose }: Props) {
  const session = useVaultSession();
  const config = useConfig();
  const [step, setStep] = useState<Step>('name');
  const [orgName, setOrgName] = useState('');
  const [rows, setRows] = useState<ScopeSeedRow[]>([EMPTY_ROW]);
  const [error, setError] = useState<string | null>(null);
  // Set the moment the vault's name is durably in place (rename or claim
  // committed), so the review step — which a failed run drops back to —
  // never promises a rename that already happened.
  const [nameCommitted, setNameCommitted] = useState(false);
  // What the finished run actually did, for the done step's honest report.
  const [doneReport, setDoneReport] = useState<{ created: number; existing: number } | null>(null);

  const seeds = seedableRows(rows);
  const dups = duplicateSeedNames(rows);
  // Names the vault already holds — the same list the run filters against.
  // `?? []`: scopes are undefined while the boot fetch is in flight, and this
  // component renders (closed) on every orgs-section mount — an unguarded
  // read would take the whole section down with it.
  const knownScopes = new Set((session.scopes ?? []).map((s) => s.name));
  const rowsValid = rows.every((r) => scopeSeedRowError(r) === null) && dups.size === 0;
  const name = orgName.trim();

  function close() {
    // Never abandon a run mid-flight: the writes are not transactional, and a
    // dialog torn down between the rename and the scope creates would hide
    // the outcome of work that already happened.
    if (step === 'running') return;
    setStep('name');
    setOrgName('');
    setRows([EMPTY_ROW]);
    setError(null);
    setNameCommitted(false);
    setDoneReport(null);
    onClose();
  }

  function setRow(i: number, patch: Partial<ScopeSeedRow>) {
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function onRun() {
    if (!name || !rowsValid) return;
    setStep('running');
    setError(null);
    try {
      let createScope: (req: ReturnType<typeof toCreateRequest>) => Promise<unknown>;
      if (ownVaultId) {
        // The signing handle is resolved INSIDE this click, before any write:
        // resolving it costs a signed `vault.current()`, and when the wallet
        // is cold that is where the step-up ceremony opens — inside the
        // gesture that asked for the run. The rename and the creates below
        // then sign with warmed keys.
        const handle = await session.signingHandle();
        await session.sdk.vault.rename(ownVaultId, name);
        setNameCommitted(true);
        session.noteVaultRenamed(name);
        createScope = (req) => handle.scopes.create(req);
      } else {
        // The claim branch exists for the surface opening PRE-claim — this
        // wizard mounted where no vault exists yet. In the assembled app it
        // is currently unreachable: vault-layout renders nothing without a
        // vault record, so by the time the wizard can open a vault is always
        // claimed and `ownVaultId` resolves. Kept rather than deleted so the
        // surface does not have to re-derive the pre-claim flow when the
        // layout gate opens.
        if (!config.gatewayPublicKey) throw new Error('Gateway public key unavailable — vault claim cannot mint a delegation token.');
        if (!config.s3Endpoint) throw new Error('S3 gateway endpoint unavailable — vault claim cannot run onboarding.');
        const claimed = await claimVault({
          wallet: session.wallet,
          vaultName: name,
          vaultBaseUrl: config.vaultBaseUrl,
          gatewayPublicKey: config.gatewayPublicKey,
          s3Endpoint: config.s3Endpoint,
          garUrl: config.garUrl,
          rpcEndpoint: config.rpcEndpoint,
        });
        setNameCommitted(true);
        createScope = (req) => claimed.handle.scopes.create(req);
      }
      // Names ARE the grant keys, so a name the session already lists IS that
      // scope — attempting it again can only 409. Filtering here (at run
      // time, against the freshly-refreshed list) is what makes a re-run
      // after a partial failure convergent instead of a loop that re-attempts
      // every row that already committed.
      const known = new Set(session.scopes.map((s) => s.name));
      const pending = seeds.filter((r) => !known.has(r.name.trim()));
      let created = 0;
      let existing = seeds.length - pending.length;
      for (const row of pending) {
        try {
          await createScope(toCreateRequest(row));
          created += 1;
        } catch (e) {
          // A 409 on one row means that scope is already there — the row
          // counts as done and is reported honestly at the end, rather than
          // aborting the run and stranding the rows after it.
          if (e instanceof VaultRequestError && e.status === 409) {
            existing += 1;
            continue;
          }
          throw e;
        }
      }
      setDoneReport({ created, existing });
      await session.refreshScopes();
      await session.refreshOrgs();
      setStep('done');
    } catch (e) {
      // Whatever committed before the failure is real work the session must
      // now reflect — without this refresh the scopes page and the switcher
      // show a world where nothing happened, and a re-run re-attempts rows
      // that already exist. Best-effort on purpose: a failing refresh must
      // not bury the error that caused the abort.
      void Promise.allSettled([session.refreshScopes(), session.refreshOrgs()]);
      // Classified, not raw: a step-up failure must read as the actionable
      // reconnect line. Back to review (not name) — everything typed so far
      // is still what the user meant.
      setError(toDisplayError(e));
      setStep('review');
    }
  }

  const railIndex = step === 'name' ? 0 : step === 'scopes' ? 1 : 2;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      title="Set up organization"
      onClose={close}
      isLoading={step === 'running'}
      customActions={
        step === 'name' ? (
          <>
            <Button variant="tertiary" onClick={close}>Cancel</Button>
            <Button variant="primary" disabled={!name} onClick={() => setStep('scopes')}>Continue</Button>
          </>
        ) : step === 'scopes' ? (
          <>
            <Button variant="tertiary" onClick={() => setStep('name')}>Back</Button>
            <Button variant="primary" disabled={!rowsValid} onClick={() => setStep('review')}>Continue</Button>
          </>
        ) : step === 'review' ? (
          <>
            <Button variant="tertiary" onClick={() => setStep('scopes')}>Back</Button>
            <Button variant="primary" onClick={() => { void onRun(); }}>Set up organization</Button>
          </>
        ) : step === 'done' ? (
          <Button variant="primary" onClick={close}>Close</Button>
        ) : null
      }
    >
      {step !== 'done' && <ConnectStepRail steps={RAIL} currentIndex={railIndex} />}

      {step === 'name' && (
        <Box className="vault-orgs__wizard-body">
          <TextField
            fullWidth
            label="Organization name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            helperText={ownVaultId
              ? 'Your vault is the organization — naming it here renames the vault.'
              : 'A vault will be claimed for this wallet and named as your organization.'}
          />
        </Box>
      )}

      {step === 'scopes' && (
        <Box className="vault-orgs__wizard-body">
          <p className="vault-orgs__wizard-sub">
            Seed the scope tree members can be granted into. The name is the permanent grant key;
            the path is presentation metadata only.
          </p>
          {rows.map((row, i) => {
            // Field-scoped errors: a missing or invalid NAME is the name
            // field's problem, a malformed path the path field's — rendering
            // one under the other misattributes it (an "enter a name" under
            // the path field reads as a path complaint).
            const nameError = row.name.trim() === '' && row.path.trim() === ''
              ? null
              : scopeSeedNameError(row.name.trim());
            const dupError = dups.has(row.name.trim()) ? 'Duplicate name — each scope needs its own.' : null;
            const nameHelper = nameError ?? dupError;
            // Info, not error: the run filters against the session's scope
            // list at commit time and a 409 counts as "already existed", so
            // an existing name is skippable — but it must SAY so, or a user
            // who cannot yet see the new scopes re-types them and reads the
            // skip as a failure to create.
            const existsNote = nameHelper === null && knownScopes.has(row.name.trim())
              ? 'Already exists in this vault — the run will skip it.'
              : null;
            const pathError = scopeSeedPathError(row.path.trim());
            return (
              <div key={i} className="vault-orgs__seed-row">
                <TextField
                  size="small"
                  label="Name"
                  value={row.name}
                  onChange={(e) => setRow(i, { name: e.target.value })}
                  error={nameHelper !== null}
                  helperText={nameHelper ?? existsNote ?? undefined}
                />
                <TextField
                  size="small"
                  label="Path (optional)"
                  value={row.path}
                  onChange={(e) => setRow(i, { path: e.target.value })}
                  error={pathError !== null}
                  helperText={pathError ?? undefined}
                />
                <Button
                  variant="tertiary"
                  aria-label="Remove scope"
                  className="vault-orgs__seed-remove"
                  // The last row cannot be removed — the step always offers at
                  // least one row, and an empty one is skipped by the run.
                  disabled={rows.length === 1}
                  onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </Button>
              </div>
            );
          })}
          <Button variant="tertiary" startIcon={<AddIcon />} onClick={() => setRows((rs) => [...rs, EMPTY_ROW])}>
            Add scope
          </Button>
        </Box>
      )}

      {step === 'review' && (
        <Box className="vault-orgs__wizard-body">
          <p className="vault-orgs__wizard-sub">
            {ownVaultId
              ? nameCommitted
                ? `Your vault is named “${name}”.`
                : `Your vault will be renamed to “${name}”.`
              : nameCommitted
                ? `Your organization's vault is claimed and named “${name}”.`
                : `A new vault will be claimed and named “${name}”.`}
          </p>
          {seeds.length === 0 ? (
            <p className="vault-orgs__wizard-sub">No scopes will be created — you can add them later on the Scopes page.</p>
          ) : (
            <ul className="vault-orgs__seed-summary">
              {seeds.map((r) => (
                <li key={r.name.trim()}><code>{r.name.trim()}</code>{r.path.trim() ? ` → ${r.path.trim()}` : ''}</li>
              ))}
            </ul>
          )}
          <AgentServiceNote />
        </Box>
      )}

      {step === 'running' && (
        <Box className="vault-orgs__wizard-body vault-orgs__wizard-body--running">
          <CircularProgress size={20} />
          <span className="vault-orgs__wizard-sub">Setting up your organization…</span>
        </Box>
      )}

      {step === 'done' && (
        <Box className="vault-orgs__wizard-body">
          <Alert severity="success">
            Your organization is ready{doneReport && doneReport.created + doneReport.existing > 0
              ? ` — ${doneReport.created} ${doneReport.created === 1 ? 'scope' : 'scopes'} created`
                + (doneReport.existing > 0 ? `, ${doneReport.existing} already existed` : '')
              : ''}.
          </Alert>
          <AgentServiceNote />
        </Box>
      )}

      {error && <Alert severity="error" className="vault-orgs__wizard-error">{error}</Alert>}
    </Dialog>
  );
}

/**
 * Plain language, no dead control: the registry-bucket (agent service) join is
 * a real later step the SDK simply cannot express yet — `memberships` and
 * `vault` expose no method for it. A disabled button would imply a capability
 * that exists; the note states the gap and leaves the action where it lives.
 */
function AgentServiceNote() {
  return (
    <Alert severity="info" className="vault-orgs__wizard-note">
      Linking an agent service — joining the organization's registry bucket — is a separate step and
      is not available in the Vault UI yet.
    </Alert>
  );
}
